import React from 'react';
import { canonicalFor, usePageMeta } from '../../hooks/usePageMeta';
import { PageHeader } from '../../components/page/PageHeader';
import { ContentSection } from '../../components/page/ContentSection';
import { WorkholdingChart } from '../../components/page/WorkholdingChart';
import { TechAssistCallout } from '../../components/page/TechAssistCallout';
import { RelatedLinks } from '../../components/page/RelatedLinks';
const checks = ['Guarding is in place and chips can evacuate safely.', 'Tooling clears the part, chuck and turret.', 'The setup is rigid and tool overhang is minimized.', 'Machine horsepower and limits suit the cut.', 'Machine, holder, chuck and shop safety procedures are followed, including required PPE.'];
export function WorkholdingSafety() {
  usePageMeta({
    title: 'Workholding & Safety for CNC Lathes | Robertson Precision',
    description: 'Shear Geometry tooling is for CNC lathes only. Workholding and setup checks plus the published security-of-workholding chart for 6061-T6 aluminum.',
    canonical: canonicalFor('/technical-resources/safety/')
  });
  return <>
      <PageHeader eyebrow="Technical Resources" title="Workholding & Safety" lead="High material-removal rates produce large, high-velocity chips. Secure workholding and a checked setup come before the first cut." crumbs={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Technical Resources',
      href: '/technical-resources/'
    }, {
      label: 'Workholding & Safety'
    }]} data-id="element-904" data-mp="39q6gzbz8rw6g">
        <p className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 border-2 border-cta px-5 py-3 font-mono text-[13px] font-semibold tracking-[0.1em] text-white sm:text-[14px]" data-id="element-905" data-mp="2qugfdpv811t3">
          <span data-id="element-906" data-mp="2zul8qw3kxzo9">CNC LATHES ONLY</span>
          <span aria-hidden="true" className="text-cta" data-id="element-907" data-mp="a485m5618b51">
            /
          </span>
          <span data-id="element-908" data-mp="f32o16037xom">NOT FOR MANUAL LATHES</span>
        </p>
      </PageHeader>

      <ContentSection id="workholding" title="Hold the workpiece securely" data-id="element-909" data-mp="f55q4oeilh9p">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-910" data-mp="2b4mkiuwz0t09">
          <div className="rp-measure space-y-4 text-[15.5px] leading-[1.7] text-graphite/85" data-id="element-911" data-mp="2755fu3pdbzw5">
            <p data-id="element-912" data-mp="2l4wywiuqs6h0">
              The workpiece must be held securely, with adequate chuck pressure
              and jaw contact to resist cutting forces. Insecure workholding can
              release the part.
            </p>
            <p data-id="element-913" data-mp="1sgrclro1i9f2">
              No acceptable chuck-pressure values are published here. Follow
              the machine, chuck and shop safety procedures for your setup.
            </p>
          </div>
          <div data-id="element-914" data-mp="226klum0pmcg5">
            <h3 className="font-display text-[19px] font-medium text-navy" data-id="element-915" data-mp="2273utd8dtqi1">
              Check before cutting
            </h3>
            <ul className="mt-4 divide-y divide-rule border-y border-rule" data-id="element-916" data-mp="1xczlinbn96ds">
              {checks.map(item => <li key={item} className="flex gap-3 py-3 text-[15px] leading-relaxed text-graphite" data-id="element-917" data-mp="14qevprath2cc">
                  <span aria-hidden="true" className="mt-2.5 h-px w-3 shrink-0 bg-copper" data-id="element-918" data-mp="3gdqs0x7876ge" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </ContentSection>

      <ContentSection id="chart" tone="warm" title="Security of workholding" data-id="element-919" data-mp="37dlo4x18qq6z">
        <WorkholdingChart data-id="element-920" data-mp="3be4cui7u7g4y" />
      </ContentSection>

      <ContentSection id="help" data-id="element-921" data-mp="3benmlbnc2y7w">
        <TechAssistCallout location="safety" showPrepList data-id="element-922" data-mp="2ykpfztukrxt" />
      </ContentSection>

      <RelatedLinks links={[{
      label: 'Aluminum',
      href: '/applications/aluminum/',
      description: '20 hp chip-control starting range.'
    }, {
      label: 'Starting Data',
      href: '/technical-resources/starting-data/',
      description: 'Published values by material.'
    }, {
      label: 'Tool Holders',
      href: '/products/holders/',
      description: 'SALXR 16-3D and SALXR 12-3B.'
    }, {
      label: 'Contact',
      href: '/contact/',
      description: 'Sales and technical assistance.'
    }]} data-id="element-923" data-mp="2whtz09xe6lp" />
    </>;
}